import { useCallback, useState, useEffect, useMemo } from 'react'
import { VersionReportVersionEntity } from '../../../../domain/entity/version-report/VersionReportVersionEntity'
import { useVersionReportVersionRepository } from '../../../../services/adaptors/versionReportVersionRepositoryAdaptor'

export type UseReportDataReturnValue = ReturnType<typeof useReportData>

export const useReportData = (templateUuid: string | undefined) => {
  const [reports, setReports] = useState<VersionReportVersionEntity[]>([])
  const [selectedReportUuid, setSelectedReportUuid] = useState<
    string | undefined
  >()
  const [loading, setLoading] = useState<boolean>(false)

  const { fetchVersions } = useVersionReportVersionRepository()

  const fetchReports = useCallback(
    async (templateUuid: string) => {
      setLoading(true)
      try {
        const response = await fetchVersions(templateUuid)
        setReports(response)
        return response
      } catch (error) {
        console.error('Failed to fetch reports:', error)
        throw error
      } finally {
        setLoading(false)
      }
    },
    [fetchVersions]
  )

  // templateUuidが変更されたときにレポートを取得
  useEffect(() => {
    if (templateUuid) {
      fetchReports(templateUuid)
    } else {
      setReports([])
      setSelectedReportUuid(undefined)
    }
  }, [templateUuid, fetchReports])

  const selectReport = useCallback((report: VersionReportVersionEntity) => {
    setSelectedReportUuid(report.uuid)
  }, [])

  const handleAfterCreateReport = useCallback(
    async (uuid: string) => {
      if (templateUuid) {
        await fetchReports(templateUuid)
        setSelectedReportUuid(uuid)
      }
    },
    [templateUuid, fetchReports]
  )

  const latestReport = useMemo(
    () =>
      reports.reduce(
        (latest, current) => {
          return !latest || current.createdAt > latest.createdAt
            ? current
            : latest
        },
        undefined as VersionReportVersionEntity | undefined
      ),
    [reports]
  )

  return {
    reports,
    selectedReportUuid,
    loading,
    latestReport,
    selectReport,
    handleAfterCreateReport,
  }
}
